import { z } from 'zod'
import { datetimeLocal } from '@forgd/shared/utils/zod'
import { ExchangeGradeEnumValues, ExchangeMcapTierEnumValues, ExchangeTypeEnumValues } from '@forgd/shared/enums'

export enum FeeCaseType {
  Best = 'Best',
  Base = 'Base',
  Worst = 'Worst',
}
export const FeeCaseTypeArray = ['Best', 'Base', 'Worst'] as const

export const TExchange = z.object({
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable().optional(),
  type: z.enum(ExchangeTypeEnumValues),
  deletedAt: datetimeLocal.nullable().optional(),
  description: z.string().nullable().optional(),
  commentary: z.string().nullable().optional(),
  rank: z.number().int().nullable().optional(),
})

export const TRankedExchange = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string(),
  type: z.enum(ExchangeTypeEnumValues),
  rank: z.number().safe().int(),
  description: z.string().nullable(),
  introCommentary: z.string().nullable(),
  grade: z.enum(ExchangeGradeEnumValues).nullable(),
  pricePerformanceRating: z.number().safe().nullable(),
  tradingKpisRating: z.number().safe().nullable(),
  costAndAccessibilityRating: z.number().safe().nullable(),
})

export type RankedExchange = z.infer<typeof TRankedExchange>

export const TExchangesRankedResponse = z.array(TRankedExchange)

export const TPreferences = z.object({
  tokenSupply: z.number().safe().int(),
  tokenFDV: z.number().safe().int(),
  tgeDate: datetimeLocal.nullable().optional(),
})
export type Preferences = z.infer<typeof TPreferences>

export const TListingFeesRequest = z.object({
  tokenFDV: z.string().transform(Number),
  exchanges: z.union([z.string().transform((val) => [val]), z.array(z.string())]).default([]),
  feeCaseType: z.enum(FeeCaseTypeArray),
})

export type ListingFeesRequest = z.infer<typeof TListingFeesRequest>

export const feeDenominationItem = z.object({
  stablecoin: z.number().safe().int(),
  token: z.number().safe().int(),
  tokenSupplyPercentage: z.number().safe(),
  totalUSD: z.number().safe().int(),
})

export const TListingFee = z.object({
  feeBreakdownByExchange: z.array(
    z.object({
      exchange: z
        .object({
          id: z.string(),
          name: z.string().nullable().optional(),
          rank: z.number().int().nullable().optional(),
        })
        .nullable()
        .optional(),
      nonRefundableFees: feeDenominationItem,
      nonRefundablePercentage: z.number().safe(),
      refundableFees: feeDenominationItem,
      refundablePercentage: z.number().safe(),
      total: feeDenominationItem,
    }),
  ),
  feeSummaryByCategory: z.object({
    listingAndIntegrationFee: z.number().safe().int(),
    marketingBudget: z.number().safe().int(),
    securityDeposit: z.number().safe().int(),
    marketMakerDeposit: z.number().safe().int(),
  }),
})

export const TListingFeesResponse = z.object({
  worst: TListingFee,
  base: TListingFee,
  best: TListingFee,
})

export const TExchangeCostBreakdown = z.object({
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable().optional(),
  rank: z.number().safe().int(),
  dueDiligenceDescription: z.string().nullable().optional(),
  hasListingFees: z.boolean(),
  hasSecurityDeposit: z.boolean(),
  estimatedListingFees: z.number().safe(),
})
export type ExchangeCostBreakdown = z.infer<typeof TExchangeCostBreakdown>

export const TCostBreakdownByExchangeResponse = z.array(TExchangeCostBreakdown)

export const TExchangeAvgPostTgePopPerformance = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  type: z.enum(ExchangeTypeEnumValues),
  pop: z.number().safe(),
})

export const TExchangeAvgPostTgePopPerformanceResponse = z.array(TExchangeAvgPostTgePopPerformance)

export type ExchangeAvgPostTgePopPerformance = z.infer<typeof TExchangeAvgPostTgePopPerformance>

export const TExchangeProfile = z.object({
  id: z.string().trim().min(1),
  name: z.string().trim().min(1),
  logoUrl: z.string().trim().min(1).nullable(),
  rank: z.number().safe().int().positive().nullable(),
  commentary: z.string().nullable(),
  grade: z.enum(ExchangeGradeEnumValues).nullable(),
  pricePerformanceRating: z.number().int().min(1).max(5).nullable(),
  tradingKpisRating: z.number().int().min(1).max(5).nullable(),
  costAndAccessibilityRating: z.number().int().min(1).max(5).nullable(),
  estimatedFees: z.number().safe().nonnegative(),
  demographics: z
    .object({
      northAmerica: z.number().min(0).max(100),
      southAmerica: z.number().min(0).max(100),
      europe: z.number().min(0).max(100),
      middleEast: z.number().min(0).max(100),
      africa: z.number().min(0).max(100),
      oceania: z.number().min(0).max(100),
      asia: z.number().min(0).max(100),
    })
    .nullable(),
})

export type ExchangeProfile = z.infer<typeof TExchangeProfile>

export const TExchangeTradingStatistics = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  mCapTier: z.enum(ExchangeMcapTierEnumValues),
  avg24hVol: z.number().safe(),
  avgAskDepthPos2: z.number().safe(),
  avgBigDepthNeg2: z.number().safe(),
  avgDepth2: z.number().safe(),
  avgSpread: z.number().safe(),
  avg24hVolRank: z.number().safe(),
  avgAskDepthPos2Rank: z.number().safe(),
  avgBigDepthNeg2Rank: z.number().safe(),
  avgDepth2Rank: z.number().safe(),
  avgSpreadRank: z.number().safe(),
})

export type ExchangeTradingStatistics = z.infer<typeof TExchangeTradingStatistics>

export const TExchangeKpiChartRecord = z.object({
  index: z.number().safe().int().nonnegative(),
  kpi: z.number().safe(),
  label: z.string(),
})

export type ExchangeKpiChartRecord = z.infer<typeof TExchangeKpiChartRecord>

export const TExchangeKpiChartResponse = z.object({
  exchangeKpiChart: z.object({
    data: z.array(TExchangeKpiChartRecord),
    meta: z.object({
      xAxis: z.string(),
      xFormat: z.string(),
      labels: z.array(z.string()),
    }),
  }),
  exchangeKpiRanks: z.array(
    z.object({
      key: z.enum(ExchangeMcapTierEnumValues),
      label: z.string().trim().min(1),
      rank: z.number().safe().int().positive(),
    }),
  ),
})

export type ExchangeKpiChartResponse = z.infer<typeof TExchangeKpiChartResponse>

export const TExchangeRoiChartRecord = z.object({
  index: z.number().safe().int().nonnegative(),
  roi: z.number().safe(),
  roiChange: z.number().safe(),
})

export type ExchangeRoiChartRecord = z.infer<typeof TExchangeRoiChartRecord>

export const TExchangeRoiChartResponse = z.object({
  exchangeRoiChart: z.object({
    data: z.array(TExchangeRoiChartRecord),
    meta: z.object({
      xAxis: z.string(),
      xFormat: z.record(z.string(), z.string().trim().min(1)),
      labels: z.array(z.string()),
    }),
  }),
  exchangeRoiRanks: z.array(
    z.object({
      key: z.string().trim().min(1),
      label: z.string().trim().min(1),
      rank: z.number().safe().int().positive(),
    }),
  ),
})

export type ExchangeRoiChartResponse = z.infer<typeof TExchangeRoiChartResponse>

export type ListingFeesRequestParams = z.infer<typeof TListingFeesRequest>

export enum QuestionsTableFilterSelectionTypes {
  EXCHANGE = 'exchange',
  CATEGORY = 'category',
  PAGE = 'page',
}

export const TQuestionsRequestParams = z.object({
  exchangeId: z.string().optional(),
  category: z.string().optional(),
  page: z.number().safe().optional(),
})
